import Phaser from 'phaser';
import { ProjectileType } from '@/components/ComponentDefs';
import { Enemy } from '@/entities/Enemy';
import { Actor } from '@/entities/Actor';
import { TILE_SIZE, TileType, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';
import { MAP_GRID, getDecorAt, damageDecor, removeDecor } from '@/map/MapData';
import { GOD } from '@/config/GodMode';
import { EventBus, GameEvents } from '@/events/EventBus';
import { VFXPool } from '@/systems/ObjectPool';

export interface ProjectileConfig {
  scene: Phaser.Scene;
  startX: number;
  startY: number;
  target: Enemy;
  damage: number;
  speed: number;            // px/s
  projectileType: ProjectileType;
  color: number;
  /** For AoE missiles: getter returning all live enemies */
  allEnemies?: () => Enemy[];
  /** Critical hit flag — affects visual feedback on impact */
  isCrit?: boolean;
  /** Piercing flag — halves target resistance on hit */
  piercing?: boolean;
  /** True if fired by a cloaked robot — triggers confusion on hit */
  fromCamo?: boolean;
  /** Homing missile: seconds between trajectory corrections (lower = better tracking) */
  homingInterval?: number;
  /** True if launched from a flying robot (affects launch direction) */
  isFlyer?: boolean;
  /** Enemy bases that can be damaged by accidental projectile hits */
  enemyBases?: Actor[];
}

// ── Per-type accuracy radii ──
// hitRadius: full damage if target within this distance at impact
// splashRadius: linear falloff damage between hitRadius and splashRadius
const PROJECTILE_ACCURACY: Record<string, { hitRadius: number; splashRadius: number }> = {
  [ProjectileType.BULLET]:      { hitRadius: 14, splashRadius: 0 },
  [ProjectileType.SHELL]:       { hitRadius: 12, splashRadius: 45 },
  [ProjectileType.MISSILE_ARC]: { hitRadius: 15, splashRadius: 80 },
  [ProjectileType.MUD_BLOB]:    { hitRadius: 16, splashRadius: 30 },
};

/**
 * Compute a predicted intercept point for a projectile targeting a moving enemy.
 * Solves: where will the enemy be when the projectile arrives?
 * Returns the predicted (px, py) world coordinates the projectile should aim at.
 */
function predictIntercept(
  sx: number, sy: number, projSpeed: number,
  tx: number, ty: number, tvx: number, tvy: number,
): { px: number; py: number } {
  // Relative position
  const dx = tx - sx;
  const dy = ty - sy;

  // Quadratic: |P + V*t|² = (projSpeed*t)²
  // (tvx²+tvy² - projSpeed²)t² + 2(dx*tvx+dy*tvy)t + (dx²+dy²) = 0
  const a = tvx * tvx + tvy * tvy - projSpeed * projSpeed;
  const b = 2 * (dx * tvx + dy * tvy);
  const c = dx * dx + dy * dy;

  let t = 0;
  if (Math.abs(a) < 0.001) {
    // Linear case (projectile speed ≈ target speed)
    if (Math.abs(b) > 0.001) {
      t = -c / b;
    }
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sqrtDisc = Math.sqrt(disc);
      const t1 = (-b - sqrtDisc) / (2 * a);
      const t2 = (-b + sqrtDisc) / (2 * a);
      // Pick smallest positive root
      if (t1 > 0 && t2 > 0) t = Math.min(t1, t2);
      else if (t1 > 0) t = t1;
      else if (t2 > 0) t = t2;
    }
  }

  // Clamp prediction time to avoid absurd lead distances (max 3 seconds)
  t = Math.max(0, Math.min(t, 3));

  return {
    px: tx + tvx * t,
    py: ty + tvy * t,
  };
}

export class Projectile {
  public scene: Phaser.Scene;
  public target: Enemy;
  public damage: number;
  public alive: boolean = true;

  // Position
  public x: number;
  public y: number;

  // Movement
  private speed: number;
  private projectileType: ProjectileType;

  // Sprite
  private sprite: Phaser.GameObjects.Image;

  // Trail
  private trailGfx: Phaser.GameObjects.Graphics | null = null;
  private trailPositions: { x: number; y: number }[] = [];
  private color: number;
  private smokeSpawnDist: number = 0; // distance since last smoke puff

  // Trajectory: projectile flies toward a fixed predicted point (no homing)
  private startX: number;
  private startY: number;
  private aimX: number;    // predicted intercept point
  private aimY: number;
  private totalDist: number;
  private progress: number = 0; // 0..1
  private arcHeight: number;

  // ── Homing missile state ──
  private homingVx: number = 0;
  private homingVy: number = 0;
  private homingSpeed: number = 0;     // reduced flight speed for homing missiles
  private homingTurnRate: number = 1.5; // rad/s — max steering per correction
  private homingTargetX: number = 0;   // snapshot of enemy position at fire time
  private homingTargetY: number = 0;
  private homingInterval: number = 0.5; // seconds between direction corrections (evo-based)
  private homingTimer: number = 0;      // countdown to next correction

  // AoE data (missiles only)
  private allEnemies?: () => Enemy[];
  private enemyBases: Actor[];

  // Critical hit data (passed from BehaviorEngine)
  public isCrit: boolean;
  public piercing: boolean;
  public fromCamo: boolean;

  // Predicted arc overlay (drawn when GOD.showMissileArc is on)
  private arcOverlayGfx: Phaser.GameObjects.Graphics | null = null;

  // Hard lifetime cap (seconds) — kills stray projectiles that spiral or miss
  private lifetime: number = 0;
  private static readonly MAX_LIFETIME = 5;

  constructor(config: ProjectileConfig) {
    this.scene = config.scene;
    this.target = config.target;
    this.damage = config.damage;
    this.speed = config.speed;
    this.projectileType = config.projectileType;
    this.allEnemies = config.allEnemies;
    this.enemyBases = config.enemyBases ?? [];
    this.isCrit = config.isCrit ?? false;
    this.piercing = config.piercing ?? false;
    this.fromCamo = config.fromCamo ?? false;

    this.x = config.startX;
    this.y = config.startY;
    this.startX = config.startX;
    this.startY = config.startY;

    // ── Missile homing: velocity-based flight with smooth continuous steering ──
    if (config.projectileType === ProjectileType.MISSILE_ARC) {
      // Snapshot enemy position at fire time — missile aims here, no recalculation
      this.homingTargetX = config.target.body.x;
      this.homingTargetY = config.target.body.y;

      // Homing correction interval scales with evo level:
      // evo0 = 0.5s (slow, easy to dodge), evo1 = 0.3s, evo2 = 0.1s (precise)
      this.homingInterval = config.homingInterval ?? 0.5;
      this.homingTimer = 0; // first correction happens immediately
      this.homingTurnRate = 2.5; // rad/s max turn per correction

      // Launch direction: flyers aim toward enemy ± 30°, ground robots launch upward ± 30°
      const spread = (Math.random() - 0.5) * (Math.PI / 3); // ± 30°
      let launchAngle: number;
      if (config.isFlyer) {
        const toEnemyAngle = Math.atan2(
          config.target.body.y - config.startY,
          config.target.body.x - config.startX,
        );
        launchAngle = toEnemyAngle + spread;
      } else {
        launchAngle = -Math.PI / 2 + spread; // upward ± 30°
      }

      // Slow missile speed for visible flight path
      this.homingSpeed = config.speed * 0.35;
      this.homingVx = Math.cos(launchAngle) * this.homingSpeed;
      this.homingVy = Math.sin(launchAngle) * this.homingSpeed;

      // Set aim point to target (used by onArrival for damage calc)
      this.aimX = config.target.body.x;
      this.aimY = config.target.body.y;
      this.totalDist = 0;
      this.arcHeight = 0;
    } else {
      // ── Predictive targeting for shells/bullets ──
      const vel = config.target.getVelocity();
      const intercept = predictIntercept(
        config.startX, config.startY, config.speed,
        config.target.body.x, config.target.body.y,
        vel.vx, vel.vy,
      );
      this.aimX = intercept.px;
      this.aimY = intercept.py;

      const dx = this.aimX - this.startX;
      const dy = this.aimY - this.startY;
      this.totalDist = Math.sqrt(dx * dx + dy * dy);

      if (config.projectileType === ProjectileType.SHELL || config.projectileType === ProjectileType.MUD_BLOB) {
        this.arcHeight = Math.max(20, this.totalDist * 0.2);
      } else {
        this.arcHeight = 0;
      }
    }

    // Create sprite from procedural texture (or spritesheet for mud blob)
    let textureKey = 'proj_bullet';
    if (this.projectileType === ProjectileType.SHELL) textureKey = 'proj_shell';
    else if (this.projectileType === ProjectileType.MISSILE_ARC) textureKey = 'proj_missile';
    else if (this.projectileType === ProjectileType.MUD_BLOB) textureKey = 'mud';

    if (this.projectileType === ProjectileType.MUD_BLOB) {
      // Mud blob uses animated spritesheet (animation registered in BootScene)
      const spr = config.scene.add.sprite(this.x, this.y, 'mud', 0);
      spr.setDepth(25);
      spr.setScale(0.08 * GOD.projectileScaleMult);
      spr.play('mud_fly');
      this.sprite = spr as unknown as Phaser.GameObjects.Image;
    } else {
      this.sprite = config.scene.add.image(this.x, this.y, textureKey);
      this.sprite.setDepth(25);
      // PNG sprites are ~124px wide; scale down to match game tile proportions
      const baseScale = this.projectileType === ProjectileType.BULLET ? 0.12
        : this.projectileType === ProjectileType.SHELL ? 0.14
        : 0.16; // missile
      this.sprite.setScale(baseScale * GOD.projectileScaleMult);
    }
    this.color = config.color;

    // Create trail graphics for luminous projectiles (not missiles/mud — they use smoke or nothing)
    if (this.projectileType !== ProjectileType.MISSILE_ARC && this.projectileType !== ProjectileType.MUD_BLOB) {
      this.trailGfx = config.scene.add.graphics().setDepth(24);
    }

    // Draw predicted arc trajectory overlay (missiles & shells only, when toggled)
    if (GOD.showMissileArc && this.arcHeight > 0) {
      this.arcOverlayGfx = config.scene.add.graphics().setDepth(23);
      this.drawArcOverlay();
    }
  }

  update(delta: number): void {
    if (!this.alive) return;

    const prevX = this.x;
    const prevY = this.y;
    const dt = delta / 1000;

    // Hard lifetime cap — missiles explode at 5s, others silently expire at 5s
    this.lifetime += dt;
    const maxLife = this.projectileType === ProjectileType.MISSILE_ARC ? 5 : Projectile.MAX_LIFETIME;
    if (this.lifetime >= maxLife) {
      if (this.projectileType === ProjectileType.MISSILE_ARC) {
        this.onArrival(); // explode with AoE at current position
      } else {
        this.alive = false;
        this.destroy();
      }
      return;
    }

    if (this.projectileType === ProjectileType.MISSILE_ARC) {
      this.updateHoming(dt);
    } else if (this.projectileType === ProjectileType.BULLET) {
      this.updateStraight(dt);
    } else {
      this.updateArc(dt); // shells + mud blobs
    }

    // Update sprite position + rotation to follow flight trajectory tangent
    this.sprite.setPosition(this.x, this.y);
    const moveDx = this.x - prevX;
    const moveDy = this.y - prevY;
    if (moveDx !== 0 || moveDy !== 0) {
      this.sprite.setRotation(Math.atan2(moveDy, moveDx));
    }

    // ── Trail ──
    if (this.projectileType === ProjectileType.MISSILE_ARC) {
      this.updateSmokeTrail(prevX, prevY);
    } else if (this.projectileType !== ProjectileType.MUD_BLOB) {
      this.updateLuminousTrail();
    }
  }

  /** Bullet: flies toward predicted aim point with light mid-flight course correction. */
  private updateStraight(dt: number): void {
    // Blend aim point toward target's actual position (light homing).
    // CORRECTION_RATE controls how fast the aim drifts toward reality (0 = no homing, 1 = full homing).
    const CORRECTION_RATE = 0.35;
    if (this.target.alive) {
      this.aimX += (this.target.body.x - this.aimX) * CORRECTION_RATE * dt;
      this.aimY += (this.target.body.y - this.aimY) * CORRECTION_RATE * dt;
    }

    const dx = this.aimX - this.x;
    const dy = this.aimY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const step = this.speed * dt;

    if (dist <= step + 4) {
      // Arrived at predicted point
      this.x = this.aimX;
      this.y = this.aimY;
      this.onArrival();
      return;
    }

    this.x += (dx / dist) * step;
    this.y += (dy / dist) * step;

    // Wall collision: bullet hits a wall tile → destroy with impact VFX
    const col = Math.floor(this.x / TILE_SIZE);
    const row = Math.floor(this.y / TILE_SIZE);
    if (MAP_GRID[row]?.[col] === TileType.WALL) {
      this.alive = false;
      this.spawnBulletImpact();
      this.destroy();
      return;
    }
  }

  /** Homing missile: interval-based direction correction (evo scales interval) */
  private updateHoming(dt: number): void {
    // Only recalculate direction at fixed intervals (not every frame)
    this.homingTimer -= dt;
    if (this.homingTimer <= 0) {
      this.homingTimer = this.homingInterval;

      const desiredAngle = Math.atan2(
        this.homingTargetY - this.y,
        this.homingTargetX - this.x,
      );
      const currentAngle = Math.atan2(this.homingVy, this.homingVx);

      let diff = desiredAngle - currentAngle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;

      // Apply turn proportional to interval — longer wait = bigger correction chunk
      const maxTurn = this.homingTurnRate * this.homingInterval;
      const turn = Math.sign(diff) * Math.min(Math.abs(diff), maxTurn);
      const newAngle = currentAngle + turn;

      this.homingVx = Math.cos(newAngle) * this.homingSpeed;
      this.homingVy = Math.sin(newAngle) * this.homingSpeed;
    }

    // Move along current velocity (constant between corrections)
    this.x += this.homingVx * dt;
    this.y += this.homingVy * dt;

    // Check proximity to snapshot target — arrive when close enough
    const dx = this.homingTargetX - this.x;
    const dy = this.homingTargetY - this.y;
    const distSq = dx * dx + dy * dy;
    const hitR = 18;
    if (distSq <= hitR * hitR) {
      this.x = this.homingTargetX;
      this.y = this.homingTargetY;
      this.onArrival();
    }
  }

  /** Shell: arc trajectory toward predicted aim point */
  private updateArc(dt: number): void {
    const effectiveDist = Math.max(this.totalDist, 10);
    this.progress += (this.speed * dt) / effectiveDist;

    if (this.progress >= 1) {
      this.progress = 1;
      this.x = this.aimX;
      this.y = this.aimY;
      this.onArrival();
      return;
    }

    const t = this.progress;
    const baseX = this.startX + (this.aimX - this.startX) * t;
    const baseY = this.startY + (this.aimY - this.startY) * t;
    const arcOffset = -this.arcHeight * 4 * t * (t - 1);

    this.x = baseX;
    this.y = baseY - arcOffset;
  }

  // ── Missile AoE constants ──
  private static readonly MISSILE_AOE_RADIUS = TILE_SIZE * 2;
  private static readonly MISSILE_STUN_DURATION = 1.5;
  private static readonly MISSILE_KNOCKBACK_TILES = 3;

  /**
   * Projectile arrived at predicted intercept point.
   * Compute proximity to actual target position and apply damage accordingly.
   */
  private onArrival(): void {
    if (!this.alive) return;
    this.alive = false;

    if (this.projectileType === ProjectileType.MISSILE_ARC) {
      this.onMissileHit();
    } else if (this.projectileType === ProjectileType.MUD_BLOB) {
      this.onMudBlobHit();
    } else {
      this.onStandardHit();
    }

    this.destroy();
  }

  /** Standard projectile (BULLET / SHELL): proximity-based damage */
  private onStandardHit(): void {
    if (!this.target.alive) {
      this.spawnBulletImpact();
      EventBus.emit(GameEvents.PROJECTILE_HIT);
      return;
    }

    const acc = PROJECTILE_ACCURACY[this.projectileType]
      ?? { hitRadius: 10, splashRadius: 0 };

    const dx = this.target.body.x - this.x;
    const dy = this.target.body.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    let didHit = false;
    if (dist <= acc.hitRadius) {
      // Direct hit — full damage
      this.target.takeDamage(this.damage, this.projectileType, this.piercing, this.isCrit, this.fromCamo);
      didHit = true;
    } else if (acc.splashRadius > 0 && dist <= acc.splashRadius) {
      // Splash zone — linear falloff
      const falloff = 1 - (dist - acc.hitRadius) / (acc.splashRadius - acc.hitRadius);
      this.target.takeDamage(this.damage * falloff, this.projectileType, this.piercing, this.isCrit, this.fromCamo);
      didHit = true;
    }
    // else: miss — no damage

    // Damage resources at impact point (small radius for standard projectiles)
    this.damageResourcesAt(this.x, this.y, this.damage, acc.splashRadius || TILE_SIZE * 0.5);
    // Collateral damage to enemy bases at impact point
    this.damageBasesAt(this.x, this.y, this.damage, acc.splashRadius);

    // Type-specific impact effect
    if (this.projectileType === ProjectileType.SHELL) {
      this.spawnShellImpact();
    } else {
      this.spawnBulletImpact();
    }
    EventBus.emit(didHit ? GameEvents.PROJECTILE_HIT : GameEvents.PROJECTILE_MISS);
  }

  /** Missile AoE: explodes at predicted point, damages all enemies in blast radius.
   *  Uses hitRadius (15px) for full damage zone, then linear falloff to aoeRadius. */
  private onMissileHit(): void {
    const aoeRadius = Projectile.MISSILE_AOE_RADIUS;
    const maxKB = Projectile.MISSILE_KNOCKBACK_TILES;
    const acc = PROJECTILE_ACCURACY[ProjectileType.MISSILE_ARC];
    const hitR = acc?.hitRadius ?? 15;

    // Primary target
    if (this.target.alive) {
      const dx = this.target.body.x - this.x;
      const dy = this.target.body.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= hitR) {
        // Direct hit — full damage, full stun, full knockback
        this.target.takeDamage(this.damage, ProjectileType.MISSILE_ARC, this.piercing, this.isCrit, this.fromCamo);
        this.target.stun(Projectile.MISSILE_STUN_DURATION);
        this.target.knockBack(maxKB, 1);
      } else if (dist <= aoeRadius) {
        // Splash zone — linear falloff from hitRadius to aoeRadius
        const falloff = 1 - (dist - hitR) / (aoeRadius - hitR);
        this.target.takeDamage(this.damage * falloff, ProjectileType.MISSILE_ARC, this.piercing, this.isCrit, this.fromCamo);
        this.target.stun(Projectile.MISSILE_STUN_DURATION * falloff);
        this.target.knockBack(maxKB, falloff);
      }
    }

    // AoE: all other enemies within blast radius
    if (this.allEnemies) {
      const nearby = this.allEnemies().filter(e => {
        if (!e.alive || e === this.target) return false;
        const dx = e.body.x - this.x;
        const dy = e.body.y - this.y;
        return (dx * dx + dy * dy) <= aoeRadius * aoeRadius;
      });

      for (const enemy of nearby) {
        const dx = enemy.body.x - this.x;
        const dy = enemy.body.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= hitR) {
          enemy.takeDamage(this.damage * 0.5, ProjectileType.MISSILE_ARC, undefined, undefined, this.fromCamo);
          enemy.stun(Projectile.MISSILE_STUN_DURATION);
          enemy.knockBack(maxKB, 1);
        } else {
          const falloff = Math.max(0, 1 - (dist - hitR) / (aoeRadius - hitR));
          enemy.takeDamage(this.damage * 0.5 * falloff, ProjectileType.MISSILE_ARC, undefined, undefined, this.fromCamo);
          enemy.stun(Projectile.MISSILE_STUN_DURATION * falloff);
          enemy.knockBack(maxKB, falloff);
        }
      }
    }

    // Damage resources in blast radius
    this.damageResourcesAt(this.x, this.y, this.damage, aoeRadius);
    // Collateral damage to enemy bases in blast radius
    this.damageBasesAt(this.x, this.y, this.damage, aoeRadius);

    this.spawnMissileImpact(aoeRadius);
    EventBus.emit(GameEvents.MISSILE_EXPLODED);
  }

  // ── Tiles eligible for mud conversion (PATH and basic ground only) ──
  private static readonly MUD_CONVERTIBLE: Set<TileType> = new Set([
    TileType.PATH, TileType.PLACEMENT, TileType.BROKEN_PLACEMENT, TileType.GRILLE, TileType.GROUND,
  ]);

  /** Mud blob: light damage + convert nearby tiles to MUD */
  private onMudBlobHit(): void {
    const acc = PROJECTILE_ACCURACY[ProjectileType.MUD_BLOB];
    const splashRadius = GOD.mudSplashRadius;

    // Damage primary target
    if (this.target.alive) {
      const dx = this.target.body.x - this.x;
      const dy = this.target.body.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= acc.hitRadius) {
        this.target.takeDamage(this.damage * GOD.mudDamageMult, ProjectileType.MUD_BLOB, false, this.isCrit, this.fromCamo);
      } else if (dist <= splashRadius) {
        const falloff = 1 - (dist - acc.hitRadius) / (splashRadius - acc.hitRadius);
        this.target.takeDamage(this.damage * GOD.mudDamageMult * falloff, ProjectileType.MUD_BLOB, false, this.isCrit, this.fromCamo);
      }
    }

    // Convert tiles within splash radius to MUD
    const col = Math.floor(this.x / TILE_SIZE);
    const row = Math.floor(this.y / TILE_SIZE);
    const tileRadius = Math.ceil(splashRadius / TILE_SIZE);
    const converted: { col: number; row: number; oldType: TileType }[] = [];

    for (let dr = -tileRadius; dr <= tileRadius; dr++) {
      for (let dc = -tileRadius; dc <= tileRadius; dc++) {
        const tc = col + dc;
        const tr = row + dr;
        if (tr < 0 || tr >= MAP_GRID.length || tc < 0 || tc >= (MAP_GRID[0]?.length ?? 0)) continue;

        // Check pixel distance from impact center to tile center
        const tileCX = tc * TILE_SIZE + TILE_SIZE / 2;
        const tileCY = tr * TILE_SIZE + TILE_SIZE / 2;
        const dx = tileCX - this.x;
        const dy = tileCY - this.y;
        if (dx * dx + dy * dy > splashRadius * splashRadius) continue;

        const currentTile = MAP_GRID[tr][tc];
        // Only convert basic ground/path tiles — not walls, water, magma, base, spawn, or already mud
        if (currentTile === TileType.MUD) continue;
        if (!Projectile.MUD_CONVERTIBLE.has(currentTile)) continue;
        // Skip tiles with decorations (resources, obstacles)
        if (getDecorAt(tc, tr)) continue;

        const oldType = currentTile;
        MAP_GRID[tr][tc] = TileType.MUD;
        converted.push({ col: tc, row: tr, oldType });
      }
    }

    // Emit event for GameScene to update visuals
    if (converted.length > 0) {
      EventBus.emit(GameEvents.MUD_TILE_CONVERTED, {
        tiles: converted,
        duration: GOD.mudTileDuration,
        impactX: this.x,
        impactY: this.y,
      });
    }

    // Spawn mud splash VFX
    this.spawnMudImpact();
    EventBus.emit(GameEvents.PROJECTILE_HIT);
  }

  /** Mud impact VFX: splat animation using boue spritesheet */
  private spawnMudImpact(): void {
    if (!this.scene || !this.scene.sys.isActive()) return;

    const splat = this.scene.add.sprite(this.x, this.y, 'boue', 0);
    splat.setDepth(20);
    splat.setScale(0.15);
    splat.setAlpha(0.9);
    splat.play('mud_splat');
    splat.once('animationcomplete', () => {
      splat.destroy();
    });

    // Small brown particles flying outward
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 15 + Math.random() * 20;
      const particle = VFXPool.acquireImage(this.x, this.y, 'fx_smoke');
      if (!particle) continue;
      particle.setDepth(21).setScale(0.3).setTint(0x886633).setAlpha(0.8);
      this.scene.tweens.add({
        targets: particle,
        x: this.x + Math.cos(angle) * dist,
        y: this.y + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.1,
        duration: 300 + Math.random() * 200,
        onComplete: () => VFXPool.releaseImage(particle),
      });
    }
  }

  // ── Enemy base collateral damage: projectiles that land near bases deal damage ──

  /** Check and damage enemy bases at impact point */
  private damageBasesAt(px: number, py: number, damage: number, radius: number): void {
    for (const base of this.enemyBases) {
      if (!base.alive) continue;
      const dx = base.x - px;
      const dy = base.y - py;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const hitR = base.collisionRadius + 5; // small grace zone
      if (dist <= hitR) {
        base.takeDamage(damage);
      } else if (radius > 0 && dist <= hitR + radius) {
        const falloff = 1 - (dist - hitR) / radius;
        base.takeDamage(damage * falloff);
      }
    }
  }

  // ── Resource damage: weapons deal reduced damage to harvestable decorations ──

  /** Damage resource decorations at impact point (with weapon resistance) */
  private damageResourcesAt(px: number, py: number, damage: number, radius: number): void {
    const resist = GOD.resourceWeaponResist;
    const reducedDmg = damage * (1 - resist);
    if (reducedDmg <= 0) return;

    const col = Math.floor(px / TILE_SIZE);
    const row = Math.floor(py / TILE_SIZE);

    // Check tiles in radius
    const tileRadius = Math.ceil(radius / TILE_SIZE);
    for (let dr = -tileRadius; dr <= tileRadius; dr++) {
      for (let dc = -tileRadius; dc <= tileRadius; dc++) {
        const tc = col + dc;
        const tr = row + dr;
        const decor = getDecorAt(tc, tr);
        if (!decor || !DECOR_RESOURCE_YIELDS[decor.type]) continue;

        // Check actual pixel distance
        const cx = tc * TILE_SIZE + TILE_SIZE / 2;
        const cy = tr * TILE_SIZE + TILE_SIZE / 2;
        const dx = cx - px;
        const dy = cy - py;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > radius) continue;

        const falloff = radius > 0 ? Math.max(0, 1 - dist / radius) : 1;
        const finalDmg = reducedDmg * falloff;
        const remaining = damageDecor(decor, finalDmg);

        // Emit mining event for HP bar
        const maxHp = DECOR_RESOURCE_YIELDS[decor.type]!.hp;
        EventBus.emit(GameEvents.DECOR_MINING, { col: tc, row: tr, hp: remaining, maxHp });

        if (remaining <= 0) {
          // Resource destroyed by weapons — no yield (wasted!)
          removeDecor(decor);
          EventBus.emit(GameEvents.DECOR_HARVESTED, {
            col: tc, row: tr, type: decor.type, co: 0, electro: 0,
          });
        }
      }
    }
  }

  // ── Type-specific impact effects ──
  // Each effect scales with damage (power) and speed for visual feedback.

  /** BULLET: quick spark flash + 2-4 tiny debris fragments radiating outward */
  private spawnBulletImpact(): void {
    if (!VFXPool.ready) return;
    const power = Math.min(this.damage / 15, 3);
    const sparkScale = 0.8 + power * 0.5;

    // Central spark
    const spark = VFXPool.acquireImage(this.x, this.y, 'fx_spark');
    spark.setDepth(26).setScale(sparkScale * 0.5).setTint(this.color);
    this.scene.tweens.add({
      targets: spark,
      scaleX: sparkScale * 1.8, scaleY: sparkScale * 1.8,
      alpha: 0,
      duration: 120 + power * 30,
      onComplete: () => VFXPool.releaseImage(spark),
    });

    // Debris fragments
    const fragCount = 2 + Math.floor(power * 1.5);
    const moveDir = Math.atan2(this.aimY - this.startY, this.aimX - this.startX);
    for (let i = 0; i < fragCount; i++) {
      const angle = moveDir + (Math.random() - 0.5) * Math.PI * 0.65;
      const dist = 6 + Math.random() * 10 * power;
      const frag = VFXPool.acquireImage(this.x, this.y, 'fx_spark');
      frag.setDepth(26).setScale(0.2 + Math.random() * 0.3).setAlpha(0.9).setTint(this.color);
      this.scene.tweens.add({
        targets: frag,
        x: this.x + Math.cos(angle) * dist,
        y: this.y + Math.sin(angle) * dist,
        alpha: 0, scaleX: 0.1, scaleY: 0.1,
        duration: 150 + Math.random() * 100,
        delay: Math.random() * 30,
        onComplete: () => VFXPool.releaseImage(frag),
      });
    }
  }

  /** SHELL: medium explosion with ground scorch ring + shrapnel debris + dust cloud */
  private spawnShellImpact(): void {
    if (!VFXPool.ready) return;
    const power = Math.min(this.damage / 20, 4);
    const baseScale = 1 + power * 0.4;

    // Bright flash core
    const core = VFXPool.acquireImage(this.x, this.y, 'fx_spark');
    core.setDepth(28).setScale(baseScale).setTint(0xffffaa);
    this.scene.tweens.add({
      targets: core,
      scaleX: baseScale * 2.5, scaleY: baseScale * 2.5,
      alpha: 0,
      duration: 140,
      onComplete: () => VFXPool.releaseImage(core),
    });

    // Explosion bloom
    const bloom = VFXPool.acquireImage(this.x, this.y, 'fx_explosion');
    bloom.setDepth(27).setScale(baseScale * 0.4).setTint(0xff8800);
    this.scene.tweens.add({
      targets: bloom,
      scaleX: baseScale * 1.6, scaleY: baseScale * 1.6,
      alpha: 0,
      duration: 280 + power * 40,
      ease: 'Power2',
      onComplete: () => VFXPool.releaseImage(bloom),
    });

    // Scorch ring (expanding dark circle) — Graphics can't be easily pooled with onUpdate, keep as-is
    const ring = this.scene.add.graphics().setDepth(25);
    ring.lineStyle(2, 0x443300, 0.5);
    ring.strokeCircle(this.x, this.y, 4);
    const impactX = this.x, impactY = this.y;
    this.scene.tweens.add({
      targets: ring, alpha: 0,
      duration: 500 + power * 100,
      onUpdate: (tween) => {
        const p = tween.progress;
        const r = 4 + p * (12 + power * 6);
        ring.clear();
        ring.lineStyle(2 - p, 0x443300, 0.4 * (1 - p));
        ring.strokeCircle(impactX, impactY, r);
      },
      onComplete: () => ring.destroy(),
    });

    // Shrapnel debris (6-10 fragments)
    const shrapCount = 5 + Math.floor(power * 2);
    for (let i = 0; i < shrapCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 10 + Math.random() * 16 * power;
      const frag = VFXPool.acquireImage(this.x, this.y, 'fx_spark');
      frag.setDepth(26).setScale(0.15 + Math.random() * 0.25).setAlpha(0.8);
      frag.setTint(Math.random() > 0.5 ? 0xffaa44 : 0xcccccc);
      this.scene.tweens.add({
        targets: frag,
        x: this.x + Math.cos(angle) * dist,
        y: this.y + Math.sin(angle) * dist - Math.random() * 8,
        alpha: 0, scaleX: 0.05, scaleY: 0.05,
        duration: 200 + Math.random() * 200,
        delay: Math.random() * 60,
        onComplete: () => VFXPool.releaseImage(frag),
      });
    }

    // Dust puffs (2-3)
    const dustCount = 2 + Math.floor(power * 0.5);
    for (let i = 0; i < dustCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const ox = Math.cos(angle) * (4 + Math.random() * 6);
      const oy = Math.sin(angle) * (4 + Math.random() * 6);
      const dust = VFXPool.acquireImage(this.x + ox, this.y + oy, 'fx_smoke');
      dust.setDepth(25).setScale(0.3 + Math.random() * 0.3).setAlpha(0.4).setTint(0xaa9966);
      this.scene.tweens.add({
        targets: dust,
        x: dust.x + ox * 2, y: dust.y + oy * 1.5 - 6,
        scaleX: 1 + power * 0.3, scaleY: 1 + power * 0.3,
        alpha: 0,
        duration: 400 + Math.random() * 200,
        delay: 40 + Math.random() * 60,
        onComplete: () => VFXPool.releaseImage(dust),
      });
    }
  }

  /** MISSILE: large explosion with fireball, shockwave ring, heavy smoke + sparks */
  private spawnMissileImpact(radius: number): void {
    if (!VFXPool.ready) return;
    const power = Math.min(this.damage / 25, 5);
    const blastScale = (radius / 32) * 0.8;

    // White-hot core flash
    const core = VFXPool.acquireImage(this.x, this.y, 'fx_spark');
    core.setDepth(29).setScale(1.5 + power * 0.5);
    this.scene.tweens.add({
      targets: core,
      scaleX: 3 + power, scaleY: 3 + power,
      alpha: 0,
      duration: 160,
      onComplete: () => VFXPool.releaseImage(core),
    });

    // Main fireball (orange → red)
    const fireball = VFXPool.acquireImage(this.x, this.y, 'fx_explosion');
    fireball.setDepth(27).setScale(blastScale * 0.5).setTint(0xff6600);
    this.scene.tweens.add({
      targets: fireball,
      scaleX: blastScale * 2, scaleY: blastScale * 2,
      alpha: 0,
      duration: 350 + power * 50,
      ease: 'Power2',
      onComplete: () => VFXPool.releaseImage(fireball),
    });

    // Secondary fireball (delayed, darker red)
    const fire2 = VFXPool.acquireImage(this.x, this.y, 'fx_explosion');
    fire2.setDepth(27).setScale(blastScale * 0.3).setTint(0xcc3300).setAlpha(0.7);
    this.scene.tweens.add({
      targets: fire2,
      scaleX: blastScale * 1.5, scaleY: blastScale * 1.5,
      alpha: 0,
      duration: 300 + power * 40,
      delay: 60,
      ease: 'Power2',
      onComplete: () => VFXPool.releaseImage(fire2),
    });

    // Shockwave ring — Graphics with onUpdate, keep as-is (hard to pool)
    const ring = this.scene.add.graphics().setDepth(28);
    const impactX = this.x, impactY = this.y;
    this.scene.tweens.add({
      targets: ring, alpha: 0,
      duration: 400 + power * 60,
      onUpdate: (tween) => {
        const p = tween.progress;
        const r = p * radius * 1.2;
        ring.clear();
        ring.lineStyle(3 * (1 - p), 0xffffff, 0.6 * (1 - p));
        ring.strokeCircle(impactX, impactY, r);
      },
      onComplete: () => ring.destroy(),
    });

    // Flying sparks / embers (8-14)
    const sparkCount = 8 + Math.floor(power * 3);
    for (let i = 0; i < sparkCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 12 + Math.random() * radius * 0.7;
      const s = VFXPool.acquireImage(this.x, this.y, 'fx_spark');
      s.setDepth(27).setScale(0.2 + Math.random() * 0.4).setAlpha(0.9);
      s.setTint(Math.random() > 0.4 ? 0xffaa44 : 0xff6622);
      this.scene.tweens.add({
        targets: s,
        x: this.x + Math.cos(angle) * dist,
        y: this.y + Math.sin(angle) * dist - 4 - Math.random() * 12,
        alpha: 0, scaleX: 0.05, scaleY: 0.05,
        duration: 250 + Math.random() * 300,
        delay: Math.random() * 80,
        onComplete: () => VFXPool.releaseImage(s),
      });
    }

    // Heavy smoke plume (6-10 puffs rising upward)
    const smokeCount = 5 + Math.floor(power * 2);
    for (let i = 0; i < smokeCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.4;
      const sx = this.x + Math.cos(angle) * dist;
      const sy = this.y + Math.sin(angle) * dist;
      const smoke = VFXPool.acquireImage(sx, sy, 'fx_smoke');
      smoke.setDepth(26).setScale(0.4 + Math.random() * 0.5).setAlpha(0.65);
      smoke.setTint(i < smokeCount / 2 ? 0x555555 : 0x333333);
      this.scene.tweens.add({
        targets: smoke,
        x: sx + Math.cos(angle) * 16,
        y: sy - 14 - Math.random() * 18,
        scaleX: 1.8 + power * 0.3, scaleY: 1.8 + power * 0.3,
        alpha: 0,
        duration: 500 + Math.random() * 400,
        delay: 30 + Math.random() * 120,
        onComplete: () => VFXPool.releaseImage(smoke),
      });
    }
  }

  // ── Trail rendering ──
  // Trail length varies by projectile power: bullet ~0.5s, shell ~1.2s, missile ~2s
  // At 60fps that's roughly 30, 72, 120 stored positions
  private get trailMaxLength(): number {
    let base: number;
    switch (this.projectileType) {
      case ProjectileType.BULLET:      base = 120; break;
      case ProjectileType.SHELL:       base = 288; break;
      case ProjectileType.MISSILE_ARC: base = 480; break;
      default:                         base = 120;
    }
    return Math.round(base * GOD.trailLengthMult);
  }

  /** Luminous trail for bullets/shells: fading polyline in weapon color */
  private updateLuminousTrail(): void {
    if (!this.trailGfx) return;
    this.trailPositions.push({ x: this.x, y: this.y });
    while (this.trailPositions.length > this.trailMaxLength) {
      this.trailPositions.shift();
    }
    this.trailGfx.clear();
    const len = this.trailPositions.length;
    if (len < 2) return;
    const lineW = this.projectileType === ProjectileType.SHELL ? 1.5 : 1;
    for (let i = 1; i < len; i++) {
      const alpha = (i / len) * 0.7;
      this.trailGfx.lineStyle(lineW, this.color, alpha);
      this.trailGfx.beginPath();
      this.trailGfx.moveTo(this.trailPositions[i - 1].x, this.trailPositions[i - 1].y);
      this.trailGfx.lineTo(this.trailPositions[i].x, this.trailPositions[i].y);
      this.trailGfx.strokePath();
    }
  }

  private static readonly SMOKE_INTERVAL = 12; // px between smoke puffs

  /** Smoke trail for missiles: spawn fx_smoke images that evaporate */
  private updateSmokeTrail(prevX: number, prevY: number): void {
    if (!VFXPool.ready) return;
    const dx = this.x - prevX;
    const dy = this.y - prevY;
    this.smokeSpawnDist += Math.sqrt(dx * dx + dy * dy);
    while (this.smokeSpawnDist >= Projectile.SMOKE_INTERVAL) {
      this.smokeSpawnDist -= Projectile.SMOKE_INTERVAL;
      const smoke = VFXPool.acquireImage(
        prevX + (Math.random() - 0.5) * 4,
        prevY + (Math.random() - 0.5) * 4,
        'fx_smoke',
      );
      smoke.setDepth(23);
      smoke.setScale(0.25 + Math.random() * 0.2);
      smoke.setAlpha(0.6);
      const initScaleX = smoke.scaleX;
      const initScaleY = smoke.scaleY;
      this.scene.tweens.add({
        targets: smoke,
        alpha: 0,
        scaleX: initScaleX * 2.5,
        scaleY: initScaleY * 2.5,
        y: smoke.y - 8 - Math.random() * 10,
        duration: 3000 + Math.random() * 1500,
        onComplete: () => VFXPool.releaseImage(smoke),
      });
    }
  }

  /** Draw the full predicted arc as a dotted line from start to aim */
  private drawArcOverlay(): void {
    const g = this.arcOverlayGfx;
    if (!g) return;
    g.clear();
    const STEPS = 30;
    const DOT_SIZE = 1.5;
    for (let i = 0; i <= STEPS; i++) {
      const t = i / STEPS;
      const bx = this.startX + (this.aimX - this.startX) * t;
      const by = this.startY + (this.aimY - this.startY) * t;
      const arc = -this.arcHeight * 4 * t * (t - 1);
      const alpha = 0.15 + 0.25 * (1 - t); // fade toward end
      // Alternate dots (skip every other for dotted look)
      if (i % 2 === 0) {
        g.fillStyle(0xff8844, alpha);
        g.fillCircle(bx, by - arc, DOT_SIZE);
      }
    }
    // Draw splash radius circle at aim point
    g.lineStyle(1, 0xff4444, 0.15);
    g.strokeCircle(this.aimX, this.aimY, PROJECTILE_ACCURACY[this.projectileType]?.splashRadius ?? 0);
  }

  destroy(): void {
    this.alive = false;
    this.sprite.destroy();
    this.trailGfx?.destroy();
    this.trailGfx = null;
    this.arcOverlayGfx?.destroy();
    this.arcOverlayGfx = null;
  }

  isOutOfBounds(maxDist: number = 1000): boolean {
    const dx = this.x - this.startX;
    const dy = this.y - this.startY;
    return (dx * dx + dy * dy) > maxDist * maxDist;
  }
}
